/**
 * <subscription-options> custom element.
 *
 * Port of Expanse's subscription UI into Horizon's component-based
 * architecture. Handles radio card selection, frequency dropdown,
 * live price updates, variant matching for bundle products, cart
 * submission with correct variant/selling_plan, and info modals.
 */

import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {'onetime'|'subscription'|'additional_subscription'} SubOption */

class SubscriptionOptions extends Component {
  /* ────────────────────────────────────────────────────────────────
   * Static identity (from block data-attributes)
   * ──────────────────────────────────────────────────────────────── */

  /** @type {string | null} */
  #sectionId = null;
  /** @type {string | null} */
  #productId = null;
  /** @type {string | null} */
  #productHandle = null;

  /** @type {'default'|'v1'|'v2'} Pricing-box visual style (from data-pricing-box-style). v1 = classic cards (ex-SeroVital v4); v2 = GF-9 buy box (ex-gf9-buybox). */
  #pricingBoxStyle = 'default';

  /** @type {string} v2 per-month unit word (from the data-price-unit host attribute ← price_unit_label metafield; 'month' default). */
  #priceUnit = 'month';

  /* ────────────────────────────────────────────────────────────────
   * Parsed JSON payloads (from snippets/subscription-product-data.liquid)
   * ──────────────────────────────────────────────────────────────── */

  /** @type {any | null} */
  #mainProduct = null;
  /** @type {any | null} */
  #additionalProduct = null;
  /** @type {any | null} */
  #context = null;

  /* ────────────────────────────────────────────────────────────────
   * Cached DOM references
   * ──────────────────────────────────────────────────────────────── */

  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLInputElement | null} */
  #sellingPlanInput = null;
  /** @type {HTMLSelectElement | null} */
  #masterSellingPlanSelect = null;
  /** @type {HTMLSelectElement | null} */
  #frequenciesSelect = null;
  /** @type {HTMLElement | null} */
  #frequenciesSelectWrapper = null;

  /* ────────────────────────────────────────────────────────────────
   * Runtime state
   * ──────────────────────────────────────────────────────────────── */

  /** @type {SubOption | null} */
  #selectedOption = null;

  /** @type {number | null} Selling plan id currently reflected in hidden input + URL. */
  #selectedSellingPlanId = null;

  /** @type {number | null} Main product's current variant id. */
  #currentMainVariantId = null;

  /** @type {number | null} URL-param selling plan, kept until validated against an allocation. */
  #urlSellingPlanId = null;

  /** @type {HTMLElement | null} Cached quantity-selector-component ref (may be null on products without quantity UI). */
  #quantitySelector = null;

  /** @type {{variantUpdate: (e:Event)=>void, keydown: (e:KeyboardEvent)=>void, modalClick: (e:MouseEvent)=>void, tileChange: (e:Event)=>void} | null} */
  #handlers = null;

  /** @type {((e: SubmitEvent) => void) | null} */
  #submitHandler = null;

  /** @type {((e: Event) => void) | null} Bound buy-once click handler (v2 only). */
  #buyOnceClickHandler = null;

  /** @type {number} Bundle cadence in months for the sv per-month price (data-sv-months host attribute). */
  #svMonths = 3;

  /** @type {MutationObserver | null} Watches <html data-bb-sv> so the Convert variation can flip the sv look at runtime. */
  #svObserver = null;

  /* ────────────────────────────────────────────────────────────────
   * Lifecycle
   * ──────────────────────────────────────────────────────────────── */

  connectedCallback() {
    super.connectedCallback();

    this.#sectionId = this.dataset.sectionId ?? null;
    this.#productId = this.dataset.productId ?? null;
    this.#productHandle = this.dataset.productHandle ?? null;
    this.#pricingBoxStyle = /** @type {'default'|'v1'|'v2'} */ (
      this.dataset.pricingBoxStyle ?? 'default'
    );
    this.#priceUnit = this.dataset.priceUnit || 'month';
    this.#svMonths = Number(this.dataset.svMonths) || 3;
    this.#syncSvFlag();
    this.#observeSvFlag();

    this.#parseJsonPayloads();
    this.#cacheDomRefs();
    this.#ensureSellingPlanInput();
    this.#bindSubmitListener();
    this.#seedInitialState();
    this.#bindEventListeners();

    // Apply seeded state to the DOM: rebuild visible frequency
    // options for the seeded option, write the hidden input value,
    // align the URL param, and paint every card's prices.
    // Subsequent radio/dropdown changes flow through the same
    // methods.
    this.#updateMasterFrequencySelect();
    this.#updateSellingPlanSelectValue();
    this.#updatePrices();
    this.#applyQuantityVisibility();
    this.#applyVariantIdOverride();
    this.#portalModals();
    this.#initBuyOnce();
    this.#placeSvAfter();

  }

  updatedCallback() {
    super.updatedCallback();
    this.#cacheDomRefs();
    this.#ensureSellingPlanInput();
    this.#bindSubmitListener();
    // Re-assert our state into the morphed DOM. Without this, a
    // buy-buttons morph after variant:update would lose our hidden
    // input value even if the input itself was re-created, and the
    // card prices would fall back to whatever the server re-rendered.
    this.#updateMasterFrequencySelect();
    this.#updateSellingPlanSelectValue();
    this.#updatePrices();
    this.#applyQuantityVisibility();
    this.#applyVariantIdOverride();
    this.#portalModals();
    this.#initBuyOnce();
    this.#placeSvAfter();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#unbindEventListeners();
    this.#svObserver?.disconnect();
    this.#svObserver = null;
    // Remove portalled modals when the component leaves the DOM.
    if (this.#sectionId) {
      document
        .querySelectorAll(`[data-subscription-popup][data-portal-for="${this.#sectionId}"]`)
        .forEach((m) => m.remove());
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * Setup helpers
   * ──────────────────────────────────────────────────────────────── */

  #parseJsonPayloads() {
    this.#mainProduct = this.#readJson('[data-subscription-main-product]');
    this.#additionalProduct = this.#readJson('[data-subscription-additional-product]');
    this.#context = this.#readJson('[data-subscription-context]');

    if (!this.#mainProduct) {
      // eslint-disable-next-line no-console
      console.warn('[subscription-options] main product JSON missing');
    }
  }

  /**
   * @param {string} selector
   * @returns {any | null}
   */
  #readJson(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent ?? 'null');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[subscription-options] failed to parse ${selector}`, err);
      return null;
    }
  }

  #cacheDomRefs() {
    this.#frequenciesSelect = /** @type {HTMLSelectElement | null} */ (
      this.querySelector('[data-variant-selling-plan-select]')
    );
    this.#frequenciesSelectWrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-variant-selling-plan-select-wrapper]')
    );
    this.#masterSellingPlanSelect = /** @type {HTMLSelectElement | null} */ (
      this.querySelector('[data-master-selling-plan-select]')
    );

    const productDetails = this.closest('.product-details') ?? document;
    const pfc = productDetails.querySelector('product-form-component');
    this.#form = /** @type {HTMLFormElement | null} */ (
      pfc?.querySelector('form[data-type="add-to-cart-form"]') ?? null
    );
    this.#sellingPlanInput = /** @type {HTMLInputElement | null} */ (
      this.#form?.querySelector('input[name="selling_plan"][data-subscription-injected]') ?? null
    );
  }

  #ensureSellingPlanInput() {
    if (!this.#form) return;
    if (this.#sellingPlanInput && this.#form.contains(this.#sellingPlanInput)) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.value = '';
    input.setAttribute('data-subscription-injected', '');
    this.#form.appendChild(input);
    this.#sellingPlanInput = input;
  }

  /**
   * Seed runtime state from the server-rendered DOM:
   *   - #selectedOption from the checked radio (server preselect).
   *   - #currentMainVariantId from <form><input name="id">.
   *   - #urlSellingPlanId from ?selling_plan= (held until validated).
   *
   * Cross-mode URL restoration (3e fix): if the URL plan exists but
   * belongs to a mode different from the server preselect, override
   * #selectedOption to the URL plan's owning mode AND tick that radio
   * BEFORE the orchestrator runs. Without this override the orchestrator
   * would search the preselected mode's allocations, fail to find the
   * URL plan, fall back to that mode's first plan, and silently rewrite
   * the URL via #updateSellingPlanParam — destroying the deep-link.
   */
  #seedInitialState() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-subscription-option-control]:checked')
    );
    if (checked) {
      this.#selectedOption = /** @type {SubOption} */ (checked.value);
    }

    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.#form?.querySelector('input[name="id"]') ?? null
    );
    const vid = Number(variantInput?.value ?? NaN);
    this.#currentMainVariantId = Number.isFinite(vid) ? vid : null;

    const params = new URLSearchParams(window.location.search);
    const raw = params.get('selling_plan');
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) this.#urlSellingPlanId = n;
    }

    // Cross-mode URL plan restoration.
    if (this.#urlSellingPlanId != null) {
      const ownerMode = this.#resolveModeForPlan(this.#urlSellingPlanId);
      if (ownerMode && ownerMode !== this.#selectedOption) {
        this.#selectedOption = ownerMode;
        // Tick the right radio visually; setting `.checked = true` on
        // a radio in a name-group automatically unchecks its siblings.
        // We don't dispatch a `change` event because the orchestrator
        // about to run reads #selectedOption directly.
        const radio = /** @type {HTMLInputElement | null} */ (
          this.querySelector(
            `[data-subscription-option-control][value="${ownerMode}"]`
          )
        );
        if (radio) radio.checked = true;
      }
    }
  }

  /**
   * Given a plan id, find which subscription mode owns it by scanning
   * each linked product's variants' allocations. Returns null when the
   * id doesn't match any allocation on any linked product (URL is stale,
   * pasted from a different product, or the plan was retired in Recharge).
   *
   * Plans in Shopify are scoped to selling-plan groups attached to
   * specific products, so a given plan id appears on at most one of
   * (main / 3-mo). We pick the first match without ambiguity.
   *
   * @param {number} planId
   * @returns {SubOption | null}
   */
  #resolveModeForPlan(planId) {
    /** @type {Array<[SubOption, any | null]>} */
    const pairs = [
      ['subscription', this.#mainProduct],
      ['additional_subscription', this.#additionalProduct],
    ];
    for (const [mode, product] of pairs) {
      if (!product) continue;
      for (const variant of product.variants ?? []) {
        for (const alloc of variant.selling_plan_allocations ?? []) {
          if (Number(alloc?.selling_plan_id) === planId) return mode;
        }
      }
    }
    return null;
  }

  /* ────────────────────────────────────────────────────────────────
   * Event wiring
   * ──────────────────────────────────────────────────────────────── */

  #bindEventListeners() {
    if (this.#handlers) return;

    this.addEventListener('change', (e) => {
      const t = /** @type {HTMLElement | null} */ (e.target);
      if (!t) return;
      if (t.matches('[data-subscription-option-control]')) {
        this.#onRadioChange(/** @type {HTMLInputElement} */ (t));
      } else if (t.matches('[data-variant-selling-plan-select]')) {
        this.#onFrequencyChange(/** @type {HTMLSelectElement} */ (t));
      }
    });

    // Delegated click handler for info-modal triggers.
    // The trigger lives inside <label>, so we MUST preventDefault
    // and stopPropagation — otherwise clicking the ⓘ button also
    // flips the radio that the label controls.
    this.addEventListener('click', (e) => {
      const t = /** @type {Element | null} */ (e.target);
      if (!(t instanceof Element)) return;
      const trigger = t.closest('[data-subscription-info-trigger]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        const id = trigger.getAttribute('data-subscription-info-trigger');
        if (id) this.#openModal(id);
      }
    });

    // Document-level click handler for close buttons / overlay clicks
    // inside the portalled modals (which now live in <body> and so won't
    // bubble up to `this`).
    const sectionId = this.#sectionId ?? '';
    const modalClick = (/** @type {MouseEvent} */ e) => {
      const t = /** @type {Element | null} */ (e.target);
      if (!(t instanceof Element)) return;
      const close = t.closest('[data-subscription-popup-close]');
      if (!close) return;
      const modal = close.closest(
        `[data-subscription-popup][data-portal-for="${sectionId}"]`
      );
      if (modal) this.#closeModal(modal);
    };
    document.addEventListener('click', modalClick);

    const variantUpdate = (/** @type {Event} */ e) => this.#onProductSelect(/** @type {any} */ (e));
    document.addEventListener(StandardEvents.productSelect, variantUpdate);

    // Document-level Escape: closes any open modal in this block.
    // Document-scoped so it fires regardless of focus location (e.g.
    // when the user has tabbed into the modal's container).
    const keydown = (/** @type {KeyboardEvent} */ e) => {
      if (e.key === 'Escape') this.#closeAllOpenModals();
    };
    document.addEventListener('keydown', keydown);

    // v2 optimistic variant update. The native <variant-picker>
    // (variant-tiles block) dispatches ProductSelectEvent immediately but
    // its promise only resolves after the section fetch + morph — a
    // visible delay before the buy box re-prices. All variant pricing
    // already lives in our parsed product JSON, so react to the tile
    // radio's change event immediately; the gallery / URL / morph still
    // follow when the fetch lands, and #onProductSelect re-runs the same
    // updaters idempotently with the authoritative payload. Document-level
    // because the tiles block is a sibling, not a descendant.
    const tileChange = (/** @type {Event} */ e) => {
      // Optimistic repricing applies to v2 (the only style that uses the
      // variant-tiles block); every method below is style-agnostic.
      if (this.#pricingBoxStyle !== 'v2') return;
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      const picker = input.closest('variant-picker');
      if (!picker || String(picker.dataset.productId ?? '') !== String(this.#productId)) return;
      const vid = Number(input.dataset.variantId);
      if (!Number.isFinite(vid) || vid === this.#currentMainVariantId) return;
      this.#currentMainVariantId = vid;
      this.#updateMasterFrequencySelect();
      this.#updateSellingPlanSelectValue();
      this.#updatePrices();
      this.#applyQuantityVisibility();
      this.#applyVariantIdOverride();
    };
    document.addEventListener('change', tileChange);

    this.#handlers = { variantUpdate, keydown, modalClick, tileChange };
  }

  #unbindEventListeners() {
    if (this.#handlers) {
      document.removeEventListener(StandardEvents.productSelect, this.#handlers.variantUpdate);
      document.removeEventListener('keydown', this.#handlers.keydown);
      document.removeEventListener('click', this.#handlers.modalClick);
      document.removeEventListener('change', this.#handlers.tileChange);
      this.#handlers = null;
    }
    this.#unbindSubmitListener();
  }

  /* ────────────────────────────────────────────────────────────────
   * Handlers
   * ──────────────────────────────────────────────────────────────── */

  /** @param {HTMLInputElement} input */
  #onRadioChange(input) {
    const next = /** @type {SubOption} */ (input.value);
    if (next === this.#selectedOption) return;
    this.#selectedOption = next;

    // When switching INTO a subscription mode, clear any stale
    // selected plan — #updateMasterFrequencySelect will pick the
    // correct first/URL-preferred plan for the new mode.
    this.#selectedSellingPlanId = null;

    this.#updateMasterFrequencySelect();
    this.#updateSellingPlanSelectValue();
    this.#updatePrices();
    this.#applyQuantityVisibility();
    this.#applyVariantIdOverride();
  }

  /** @param {HTMLSelectElement} select */
  #onFrequencyChange(select) {
    const opt = select.selectedOptions[0];
    if (!opt) return;
    const planId = Number(opt.dataset.sellingPlanId);
    if (!Number.isFinite(planId)) return;
    this.#selectedSellingPlanId = planId;
    // Once the user explicitly picks, drop any URL-seeded plan so
    // subsequent rebuilds don't re-snap back.
    this.#urlSellingPlanId = null;

    this.#updateSellingPlanSelectValue();
    this.#updatePrices();
  }

  /**
   * React to variant changes on the MAIN product. Same-product variant
   * swaps morph only the variant-picker + product-form-buttons subtree;
   * our block is a sibling and stays intact, so updatedCallback does
   * not fire. We own the full rehydration path here.
   *
   * Horizon ≥4.x replaced ThemeEvents.variantUpdate with the Shopify
   * standard ProductSelectEvent ('shopify:product:select'), dispatched
   * synchronously by <variant-picker> when a selection is made; the new
   * variant data arrives asynchronously via `event.promise`, which
   * resolves only AFTER the section fetch + morph — the same post-morph
   * timing the old variantUpdate event had:
   *
   *   1. Sync filter: skip product-card pickers (quick-add) — same guard
   *      as the native 4.x consumers — and events for other products
   *      (combined-listing navigations trigger a `<main>` morph that
   *      re-mounts this element anyway).
   *   2. Await the promise; update #currentMainVariantId from
   *      result.detail.resource.id (the variant JSON — numeric id, same
   *      shape the old event carried in detail.resource).
   *   3. Re-acquire DOM refs + re-inject the hidden `selling_plan` input
   *      (buy-buttons morph wipes it along with `<input name="id">`).
   *   4. Re-run orchestrator chain → bundle cards re-match by option
   *      values against the new main variant; all cards re-price.
   *
   * @param {import('./standard-events.js').ProductSelectEvent | any} e
   */
  #onProductSelect(e) {
    if (e.target instanceof Element && e.target.closest('product-card')) return;

    // e.product.id arrives as a GID ("gid://shopify/Product/123…") while our
    // data attribute is the bare numeric id — compare numeric tails. (QA
    // 2026-07-27: the raw comparison never matched, so this handler bailed on
    // every variant change and the native product-form kept the last word on
    // input[name="id"] — a 422 for bundle-mode ATC after a variant switch.)
    const pid = (/** @type {any} */ v) => String(v ?? '').split('/').pop();
    const eventProductId = pid(e.product?.id);
    if (!eventProductId || eventProductId !== pid(this.#productId)) return;

    const promise = e.promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then((/** @type {any} */ result) => {
        const detail = result?.detail;
        if (detail?.productId && pid(detail.productId) !== pid(this.#productId)) return;

        const newVid = Number(detail?.resource?.id);

        // Native product-form.js subscribes to this same promise and writes
        // the MAIN variant id into input[name="id"] synchronously in its own
        // .then (product-form.js #onProductSelect). Defer our re-assert one
        // macrotask so #applyVariantIdOverride always runs AFTER that write —
        // otherwise bundle mode loses the input to the native handler.
        setTimeout(() => {
          // A morph re-mount replaces this instance; a stale resolve on the
          // torn-down one must not touch the DOM.
          if (!this.isConnected) return;

          if (Number.isFinite(newVid)) {
            this.#currentMainVariantId = newVid;
          }
          // Note: we do NOT fall back to reading [name="id"] from the form here
          // because #applyVariantIdOverride may have written a bundle variant ID
          // into that input. The event payload is the authoritative source.

          this.#cacheDomRefs();
          this.#ensureSellingPlanInput();
          this.#bindSubmitListener();

          this.#updateMasterFrequencySelect();
          this.#updateSellingPlanSelectValue();
          this.#updatePrices();
          this.#applyQuantityVisibility();
          this.#applyVariantIdOverride();
        }, 0);
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[subscription-options] product-select promise rejected:', error);
        }
      });
  }

  /* ────────────────────────────────────────────────────────────────
   * Orchestrator: rebuilds visible frequency select for the current
   * mode, picks a selected plan, and updates internal state.
   *
   * After this runs, #selectedSellingPlanId points at the plan that
   * the hidden <input name="selling_plan"> + master select should
   * reflect. Actual DOM write happens in #updateSellingPlanSelectValue.
   * ──────────────────────────────────────────────────────────────── */
  #updateMasterFrequencySelect() {
    const mode = this.#selectedOption;
    if (!mode) return;

    // Figure out which product + variant sources allocations for this mode.
    const sourceProduct = this.#sourceProductFor(mode);
    const targetVariant = this.#targetVariantFor(mode);
    const allocations = targetVariant?.selling_plan_allocations ?? [];

    // One-time carries no plan — clear selection and hide the wrapper.
    if (mode === 'onetime' || allocations.length === 0) {
      this.#selectedSellingPlanId = null;
      this.#setFrequencyWrapperHidden(true);
      // Keep visible <select> intact shape-wise but wipe its options
      // so stale entries don't confuse accessibility tools.
      if (this.#frequenciesSelect) this.#frequenciesSelect.innerHTML = '';
      return;
    }

    // Rebuild visible select options to match the allocations of the target variant.
    const planLookup = this.#buildPlanLookup(sourceProduct);
    this.#rebuildFrequencyOptions(targetVariant, allocations, planLookup);

    // Pick which plan is selected:
    //   1. URL param if it matches one of these allocations.
    //   2. Recommended position (product-level context value — see
    //      #recommendedPosition).
    //   3. First allocation.
    let picked = null;

    if (this.#urlSellingPlanId) {
      const match = allocations.find(
        (a) => Number(a?.selling_plan_id) === this.#urlSellingPlanId
      );
      if (match) picked = Number(match.selling_plan_id);
    }

    if (picked == null) {
      const recommended = this.#recommendedPosition();
      if (recommended <= allocations.length) {
        picked = Number(allocations[recommended - 1]?.selling_plan_id);
      }
    }

    if (picked == null && allocations[0]?.selling_plan_id != null) {
      picked = Number(allocations[0].selling_plan_id);
    }

    this.#selectedSellingPlanId = Number.isFinite(picked) ? picked : null;

    // Reflect the pick in the visible <select> so the user sees the
    // correct choice highlighted.
    if (this.#frequenciesSelect && this.#selectedSellingPlanId != null) {
      const val = String(this.#selectedSellingPlanId);
      for (const o of this.#frequenciesSelect.options) {
        if (o.value === val) {
          o.selected = true;
          break;
        }
      }
    }

    // Hide the wrapper when there's no meaningful choice.
    this.#setFrequencyWrapperHidden(allocations.length < 2);
  }

  /**
   * Resolve the variant whose selling_plan_allocations drive the
   * current mode.
   * @param {SubOption} mode
   * @returns {any | null}
   */
  #targetVariantFor(mode) {
    const src = this.#sourceProductFor(mode);
    if (!src) return null;

    if (mode === 'subscription') {
      return (
        src.variants.find((v) => Number(v.id) === this.#currentMainVariantId) ??
        src.variants[0] ??
        null
      );
    }

    // 3-mo bundle: match the main variant's option1/2/3
    // case-insensitively against the bundle product's variants, falling
    // back to the first variant with allocations.
    const mainVariant = this.#mainProduct?.variants?.find(
      (v) => Number(v.id) === this.#currentMainVariantId
    ) ?? null;
    return this.#bundleVariantByOptions(src, mainVariant);
  }

  /**
   * Resolve a bundle product's variant by matching option1/option2/option3
   * case-insensitively against the main variant's option values. Ported
   * from Expanse's `findAdditionalProductVariant`
   * (theme.js 8254-8308). Falls back to the first variant with allocations,
   * then to the first variant.
   * @param {any | null} bundleProduct
   * @param {any | null} mainVariant
   * @returns {any | null}
   */
  #bundleVariantByOptions(bundleProduct, mainVariant) {
    const variants = bundleProduct?.variants ?? [];
    if (variants.length === 0) return null;

    const firstWithAllocs = () =>
      variants.find(
        (v) => Array.isArray(v.selling_plan_allocations) && v.selling_plan_allocations.length > 0
      ) ?? variants[0] ?? null;

    if (!mainVariant) return firstWithAllocs();

    const norm = (x) => (x == null ? '' : String(x).trim().toLowerCase());
    const o1 = norm(mainVariant.option1);
    const o2 = norm(mainVariant.option2);
    const o3 = norm(mainVariant.option3);

    // Match across all option positions that the main variant carries.
    // A bundle variant only needs to match the positions the main variant
    // defines (single-option products still match on option1 alone).
    const match = variants.find((v) => {
      if (o1 !== '' && norm(v.option1) !== o1) return false;
      if (o2 !== '' && norm(v.option2) !== o2) return false;
      if (o3 !== '' && norm(v.option3) !== o3) return false;
      return true;
    });

    return match ?? firstWithAllocs();
  }

  /**
   * Resolve the product object (main / 3-mo bundle) that
   * owns the allocations + selling_plan_groups for a given mode.
   * @param {SubOption} mode
   * @returns {any | null}
   */
  #sourceProductFor(mode) {
    switch (mode) {
      case 'subscription':
        return this.#mainProduct;
      case 'additional_subscription':
        return this.#additionalProduct;
      case 'onetime':
      default:
        return null;
    }
  }

  /**
   * Build a Map<string plan_id, { id, name, group_id }> from a
   * product's selling_plan_groups. Allocations reference plans via
   * flat `selling_plan_id` / `selling_plan_group_id` (Horizon's native
   * `{{ product | json }}` schema); plan names/options live here.
   * @param {any | null} product
   * @returns {Map<string, { id:number, name:string, group_id:string }>}
   */
  #buildPlanLookup(product) {
    const map = new Map();
    const groups = product?.selling_plan_groups ?? [];
    for (const g of groups) {
      const gid = g?.id ?? null;
      for (const p of g?.selling_plans ?? []) {
        if (p?.id == null) continue;
        map.set(String(p.id), {
          id: Number(p.id),
          name: String(p.name ?? ''),
          group_id: gid != null ? String(gid) : '',
        });
      }
    }
    return map;
  }

  /* ────────────────────────────────────────────────────────────────
   * Shared helpers
   * ──────────────────────────────────────────────────────────────── */

  /**
   * v2 unit suffix, e.g. '/month' (price_unit_label metafield via the
   * data-price-unit host attribute — the Liquid unit spans must emit
   * the same word; keep in lockstep).
   *
   * Currently UNUSED: the v2 monthly card dropped its unit suffix on
   * 2026-07-29 (user tweak — live shows bare $79/$99). Kept, with the
   * data-price-unit plumbing, for any style that reinstates a unit.
   * @returns {string}
   */
  #unitSuffix() {
    return '/' + this.#priceUnit;
  }

  /**
   * Recommended frequency position (1-based): the product-level
   * `recommended_delivery_frequency_option` metafield via the context
   * JSON, else 1. Callers validate against allocations.length and
   * fall back to the first allocation.
   * @returns {number}
   */
  #recommendedPosition() {
    const prodRec = Number(this.#context?.recommended_delivery_frequency_option ?? 0);
    if (Number.isFinite(prodRec) && prodRec >= 1) return prodRec;
    return 1;
  }

  /**
   * Replace the visible frequency <select> options with the given
   * allocations. Attributes match what the Liquid renders so price
   * readers can use the same selectors regardless of whether the
   * options were server-rendered or JS-rebuilt.
   *
   * Horizon schema note: allocations carry flat `selling_plan_id` +
   * `selling_plan_group_id`; the plan name / options live in the
   * source product's `selling_plan_groups[].selling_plans[]`. Look up
   * via the planLookup map built from the same product.
   *
   * @param {any} variant
   * @param {any[]} allocations
   * @param {Map<string, {id:number,name:string,group_id:string}>} planLookup
   */
  #rebuildFrequencyOptions(variant, allocations, planLookup) {
    const select = this.#frequenciesSelect;
    if (!select) return;

    const recommended = this.#recommendedPosition();
    const recIdx = recommended <= allocations.length
      ? recommended - 1
      : 0; // Default to first plan when metafield is blank/out-of-range.

    const frag = document.createDocumentFragment();
    allocations.forEach((alloc, i) => {
      const planId = alloc?.selling_plan_id;
      if (planId == null) return;
      const plan = planLookup.get(String(planId));
      if (!plan) return;

      const o = document.createElement('option');
      o.value = String(planId);
      o.dataset.variantId = String(variant.id);
      o.dataset.sellingPlanId = String(planId);
      if (alloc.price != null) o.dataset.sellingPlanPrice = String(alloc.price);
      if (alloc.compare_at_price != null) {
        o.dataset.sellingPlanCompareAtPrice = String(alloc.compare_at_price);
      }
      const gid = alloc?.selling_plan_group_id ?? plan.group_id;
      if (gid) o.dataset.sellingPlanGroupId = String(gid);

      const adj = alloc.price_adjustments?.[0];
      if (adj) {
        if (adj.value != null) o.dataset.sellingPlanAdjustmentsValue = String(adj.value);
        if (adj.value_type != null) {
          // Liquid emits `data-selling-plan-adjustments-value_type` — preserve the underscore.
          o.setAttribute('data-selling-plan-adjustments-value_type', String(adj.value_type));
        }
      }
      const isRec = i === recIdx;
      if (isRec) o.dataset.recommended = 'true';
      o.textContent = isRec ? `${plan.name} (Recommended)` : plan.name;
      frag.appendChild(o);
    });

    // Swap atomically so assistive tech sees a single update.
    select.innerHTML = '';
    select.appendChild(frag);
  }

  /** @param {boolean} hidden */
  #setFrequencyWrapperHidden(hidden) {
    const w = this.#frequenciesSelectWrapper;
    if (!w) return;
    if (hidden) {
      w.setAttribute('hidden', '');
    } else {
      w.removeAttribute('hidden');
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * Writer: push #selectedSellingPlanId into the hidden input, master
   * select, and URL. Also handles the one-time → empty-plan case.
   * ──────────────────────────────────────────────────────────────── */
  #updateSellingPlanSelectValue() {
    const id = this.#selectedSellingPlanId;

    // Hidden input value — the one thing that matters for cart submission.
    if (this.#sellingPlanInput) {
      this.#sellingPlanInput.value = id != null ? String(id) : '';
    }

    // Master select: mark the matching <option> (by both variant id
    // and selling plan id) selected. For one-time, select the empty
    // sentinel option (value="", data-selling-plan-id="onetime").
    this.#syncMasterSelect(id);

    // URL sync.
    this.#updateSellingPlanParam(id);
  }

  /** @param {number | null} id */
  #syncMasterSelect(id) {
    const master = this.#masterSellingPlanSelect;
    if (!master) return;

    if (id == null) {
      // One-time: select the sentinel <option value="" data-selling-plan-id="onetime">.
      for (const o of master.options) {
        if (o.dataset.sellingPlanId === 'onetime') {
          o.selected = true;
          return;
        }
      }
      master.selectedIndex = 0;
      return;
    }

    // Prefer the option whose variant id matches the target variant
    // for the current mode — avoids picking the main-product option
    // when the user is in a bundle mode (same plan id could exist on
    // both products in theory; match both axes to disambiguate).
    const effMode = this.#selectedOption;
    const targetVariant = effMode ? this.#targetVariantFor(effMode) : null;
    const targetVid = targetVariant ? String(targetVariant.id) : null;
    const sid = String(id);

    let fallback = null;
    for (const o of master.options) {
      if (o.dataset.sellingPlanId === sid) {
        if (!targetVid || o.dataset.variantId === targetVid) {
          o.selected = true;
          return;
        }
        fallback ??= o;
      }
    }
    if (fallback) fallback.selected = true;
  }

  /** @param {number | null} id */
  #updateSellingPlanParam(id) {
    const url = new URL(window.location.href);
    if (id == null) {
      url.searchParams.delete('selling_plan');
    } else {
      url.searchParams.set('selling_plan', String(id));
    }
    // replaceState (not pushState) — matches variant-picker.js pattern;
    // subscription changes shouldn't pollute history.
    window.history.replaceState({}, '', url.toString());
  }

  /* ────────────────────────────────────────────────────────────────
   * Price updaters
   *
   * Every card shows a live price regardless of which radio is
   * checked, so the user can compare values before committing.
   *   - Display price = the allocation that WOULD be used if the
   *     user picked that card (selected plan if mode matches, else
   *     recommended / first allocation on that card's target variant).
   *   - Compare-at span = variant.compare_at_price (MSRP). Shown
   *     only when compare_at_price > variant.price — matches the
   *     server-render gate in Liquid so JS and SSR stay in agreement.
   *
   * Each card's markup is regenerated client-side to match
   * `snippets/subscription-price.liquid` byte-for-byte so the visual
   * structure (dollars + optional <sup>cents</sup>) is consistent
   * across transitions.
   * ──────────────────────────────────────────────────────────────── */
  #updatePrices() {
    this.#updateOnetimePrices();
    this.#updateSubscriptionPrices();
    this.#updateAdditionalSubscriptionPrices();
    this.#updateBuyOncePrice();
    this.#updateSvPrices();
    this.#updateAtcPrice();
  }

  #updateOnetimePrices() {
    const variant = this.#mainVariant();
    if (!variant) return;
    this.#renderPrice(this.querySelector('[data-onetime-price]'), variant.price);
    this.#renderCompareAt(
      this.querySelector('[data-onetime-compare-at-price]'),
      variant.compare_at_price,
      variant.price
    );
  }

  #updateSubscriptionPrices() {
    const variant = this.#mainVariant();
    if (!variant) return;
    const alloc = this.#pickDisplayAllocation('subscription', variant);
    const displayPrice = alloc?.price ?? variant.price;

    if (this.#pricingBoxStyle === 'v2') {
      // v2 (GF-9): monthly allocation price as-is, NO unit suffix — the
      // live boxes show bare $79/$99 and the Liquid v2 arm dropped its
      // unit span on 2026-07-29 (user tweak); keep in lockstep. The
      // monthly compare-at slot is CSS-hidden for this style (the live
      // GF-9 reference shows the red SAVE benefits line instead of a
      // struck price), but keep it data-correct for accessibility.
      this.#renderPrice(
        this.querySelector('[data-subscription-price]'),
        displayPrice
      );
      this.#renderCompareAt(
        this.querySelector('[data-subscription-compare-at-price]'),
        variant.price,
        displayPrice
      );
      return;
    }

    this.#renderPrice(
      this.querySelector('[data-subscription-price]'),
      displayPrice
    );
    if (this.#pricingBoxStyle === 'v1') {
      // v1: struck value prefers the admin compare-at (the bundle-product
      // SKUs set one; live Expanse strikes it on BOTH cards), falling back
      // to the regular one-time price. Gate against the allocation price so
      // the strikethrough always shows the full price. Lockstep with the
      // Liquid v1 arm (v1_sub_compare_at).
      const v1CompareAt =
        variant.compare_at_price != null && variant.compare_at_price > variant.price
          ? variant.compare_at_price
          : variant.price;
      this.#renderCompareAt(
        this.querySelector('[data-subscription-compare-at-price]'),
        v1CompareAt,
        displayPrice     // gateCents = allocation/subscription price
      );
    } else {
      // Compare-at gate uses variant.price (MSRP vs regular), not the
      // allocation price — matches Liquid's `compare_at > variant.price`
      // guard. Subscription's allocation discount is ADDITIONAL on top.
      this.#renderCompareAt(
        this.querySelector('[data-subscription-compare-at-price]'),
        variant.compare_at_price,
        variant.price
      );
    }
  }

  #updateAdditionalSubscriptionPrices() {
    if (!this.#additionalProduct) return;
    const variant = this.#targetVariantFor('additional_subscription');
    if (!variant) return;
    const alloc = this.#pickDisplayAllocation('additional_subscription', variant);
    const displayPrice = alloc?.price ?? variant.price;
    this.#renderPrice(
      this.querySelector('[data-additional-subscription-price]'),
      displayPrice
    );
    this.#renderCompareAt(
      this.querySelector('[data-additional-subscription-compare-at-price]'),
      variant.compare_at_price,
      variant.price
    );
  }

  /* ────────────────────────────────────────────────────────────────
   * v2 — "One Time Purchase - $X" link
   *
   * Rendered inside <subscription-options> by Liquid, then moved by
   * JS to after the ATC button so it sits below the primary CTA.
   * The wrapper is found by [data-buy-once-section] (section-scoped)
   * so it survives being outside the custom element's own subtree.
   * ──────────────────────────────────────────────────────────────── */

  /** @returns {HTMLElement | null} */
  #findBuyOnceWrapper() {
    if (!this.#sectionId) return null;
    return /** @type {HTMLElement | null} */ (
      document.querySelector(`[data-buy-once-section="${this.#sectionId}"]`)
    );
  }

  /**
   * Move the buy-once wrapper to after the ATC submit button's
   * block-level container. Safe to call multiple times — skips if
   * the wrapper is already outside this element.
   */
  #placeBuyOnce() {
    const wrapper = this.#findBuyOnceWrapper();
    if (!wrapper) return;
    // Already placed outside — nothing to do.
    if (!this.contains(wrapper)) return;

    const submitBtn =
      this.#form?.querySelector('button[type="submit"]') ??
      this.#form?.querySelector('input[type="submit"]');
    if (!submitBtn) return;

    const myParent = this.parentElement;
    if (!myParent) return;

    // Walk up from submit button to find the ancestor that shares
    // our parent — i.e. the sibling block that contains the ATC button.
    let ancestor = submitBtn.parentElement;
    while (ancestor && ancestor.parentElement !== myParent) {
      ancestor = ancestor.parentElement;
    }
    (ancestor ?? submitBtn.parentElement)?.after(wrapper);
  }

  /**
   * Update the price text inside the buy-once button.
   * Skips if the button is in success/loading state (disabled).
   */
  #updateBuyOncePrice() {
    const wrapper = this.#findBuyOnceWrapper();
    if (!wrapper) return;
    const btn = /** @type {HTMLButtonElement | null} */ (wrapper.querySelector('[data-buy-once-btn]'));
    if (btn?.disabled) return;
    const priceEl = wrapper.querySelector('[data-buy-once-price]');
    if (!priceEl) return;
    const variant = this.#mainVariant();
    if (!variant) return;
    // Format as dollars-only (no cents) to match the compact link style.
    const formatted = this.#formatMoney(variant.price).trim();
    const idx = formatted.lastIndexOf('.');
    const dollars = idx === -1 ? formatted : formatted.slice(0, idx);
    priceEl.innerHTML = `<span class="subscription-option__price-dollars">${this.#escape(dollars)}</span>`;
  }

  /**
   * Wire up the buy-once click handler and place the wrapper.
   * Called from connectedCallback and updatedCallback.
   */
  #initBuyOnce() {
    // v2 — and any style under sv — moves the one-time option out of the
    // cards and into a link below the ATC button. On v1/default the Liquid
    // renders the wrapper with data-sv-only so the control never shows it.
    if (this.#pricingBoxStyle !== 'v2' && !this.#isSv()) return;
    this.#placeBuyOnce();
    this.#updateBuyOncePrice();
    const wrapper = this.#findBuyOnceWrapper();
    if (!wrapper) return;
    const btn = wrapper.querySelector('[data-buy-once-btn]');
    if (!btn || btn.dataset.buyOnceListenerAttached) return;
    btn.dataset.buyOnceListenerAttached = 'true';
    this.#buyOnceClickHandler = (e) => this.#handleBuyOnceClick(e);
    btn.addEventListener('click', this.#buyOnceClickHandler);
  }

  /** @param {Event} _e */
  async #handleBuyOnceClick(_e) {
    const wrapper = this.#findBuyOnceWrapper();
    if (!wrapper) return;
    const btn = /** @type {HTMLButtonElement | null} */ (wrapper.querySelector('[data-buy-once-btn]'));
    const msgEl = wrapper.querySelector('[data-buy-once-msg]');
    if (!btn) return;

    const variant = this.#mainVariant();
    if (!variant) return;

    // Save original markup so we can restore it on error.
    const originalHTML = btn.innerHTML;

    btn.disabled = true;
    btn.classList.add('subscription-options__buy-once-btn--loading');
    btn.innerHTML = 'Adding…';
    if (msgEl) msgEl.hidden = true;

    // Horizon ≥4.x cart surfaces (cart icon, drawer) listen for the Shopify
    // standard CartLinesUpdateEvent — the old 'cart:update' CustomEvent has
    // no listeners any more. Dispatch before the request (listeners
    // subscribe to the promise at dispatch time), resolve after.
    const deferredCartUpdate = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferredCartUpdate.promise,
      })
    );

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const error = new Error(data.description ?? data.message ?? 'Could not add to cart.');
        deferredCartUpdate.reject(error);
        throw error;
      }

      btn.classList.remove('subscription-options__buy-once-btn--loading');
      btn.textContent = 'Product added to cart';

      // Fetch the updated cart and resolve the standard-event promise so
      // the cart icon count and cart drawer both reflect the new item.
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = cartRes.ok ? await cartRes.json() : null;
        if (cart) {
          deferredCartUpdate.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              didError: false,
              items: cart.items,
              source: 'subscription-options',
              sourceId: String(this.#sectionId ?? ''),
              itemCount: 1,
              productId: String(this.#productId ?? ''),
            },
          });
        } else {
          deferredCartUpdate.reject(new Error('Cart refresh failed after add.'));
        }
      } catch (refreshErr) {
        // Cart refresh failure is non-critical — item was already added.
        deferredCartUpdate.reject(refreshErr);
      }
    } catch (err) {
      deferredCartUpdate.reject(err); // no-op if already settled
      btn.disabled = false;
      btn.classList.remove('subscription-options__buy-once-btn--loading');
      btn.innerHTML = originalHTML;
      if (msgEl) {
        msgEl.hidden = false;
        msgEl.textContent =
          err instanceof Error ? err.message : 'Something went wrong. Please try again.';
        msgEl.className =
          'subscription-options__buy-once-msg subscription-options__buy-once-msg--error';
      }
    }
  }


  /* ────────────────────────────────────────────────────────────────
   * "sv" variant — SeroVital-style buy box layered on v2
   *
   * Active when pricing_box_style is v2 AND the host carries data-bb-sv.
   * Liquid sets the attribute for pricing_box_style 'v2-sv'; the Convert
   * A/B variation sets <html data-bb-sv> and we mirror it here (the block
   * also has an inline script for the pre-upgrade paint). The cards show
   * PER-MONTH figures (bundle total ÷ data-sv-months) with the regular
   * monthly price struck; the ATC button shows the selected option's
   * total and compare-at. Markup matches snippets/subscription-sv-pm.liquid.
   * ──────────────────────────────────────────────────────────────── */

  #isSv() {
    return this.hasAttribute('data-bb-sv');
  }

  #syncSvFlag() {
    if (document.documentElement.hasAttribute('data-bb-sv')) {
      this.setAttribute('data-bb-sv', '');
    }
    this.#applySvSelection();
  }

  /**
   * sv hides the one-time CARD whenever a subscription card exists (the
   * one-time path is the buy-once link instead), so a page that loaded
   * with one-time pre-checked would otherwise show no selected card and
   * add a one-time item. Move the selection to the subscription card.
   */
  #applySvSelection() {
    if (!this.#isSv() || this.#selectedOption !== 'onetime') return;
    const sub = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-subscription-option-control][value="subscription"]')
    );
    if (!sub) return;
    sub.checked = true;
    sub.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #observeSvFlag() {
    if (this.#svObserver) return;
    this.#svObserver = new MutationObserver(() => {
      const on = document.documentElement.hasAttribute('data-bb-sv');
      if (on === this.hasAttribute('data-bb-sv')) return;
      this.toggleAttribute('data-bb-sv', on);
      this.#applySvSelection();
      this.#updatePrices();
      this.#initBuyOnce();
      this.#placeSvAfter();
    });
    this.#svObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bb-sv'],
    });
  }

  /**
   * Per-month markup: dollars, superscript cents only when non-zero, /unit.
   * @param {number} priceInCents
   * @returns {string}
   */
  #svPmMarkup(priceInCents) {
    const formatted = this.#formatMoney(priceInCents).trim();
    const idx = formatted.lastIndexOf('.');
    const dollars = idx === -1 ? formatted : formatted.slice(0, idx);
    const cents = idx === -1 ? '' : formatted.slice(idx + 1);
    const centsMarkup =
      cents && cents !== '00'
        ? `<sup class="subscription-option__price-cents">.${this.#escape(cents)}</sup>`
        : '';
    return (
      `<span class="subscription-option__price-dollars">${this.#escape(dollars)}${centsMarkup}</span>` +
      `<span class="subscription-option__price-unit">/${this.#escape(this.#priceUnit)}</span>`
    );
  }

  /**
   * @param {Element | null} span
   * @param {number | null} cents
   */
  #renderSvPm(span, cents) {
    if (!span) return;
    span.innerHTML = cents == null ? '' : this.#svPmMarkup(cents);
  }

  /**
   * @param {Element | null} span
   * @param {number | null} compareCents
   * @param {number | null} gateCents
   */
  #renderSvPmCompare(span, compareCents, gateCents) {
    if (!span) return;
    if (compareCents == null || gateCents == null || compareCents <= gateCents) {
      span.setAttribute('hidden', '');
      span.innerHTML = '';
      return;
    }
    span.removeAttribute('hidden');
    span.innerHTML = this.#svPmMarkup(compareCents);
  }

  #updateSvPrices() {
    const variant = this.#mainVariant();
    if (!variant) return;
    const regular = variant.price;

    this.#toggleSvGwp('onetime', variant.price);
    const subAlloc = this.#pickDisplayAllocation('subscription', variant);
    const subPm = subAlloc?.price ?? variant.price;
    this.#toggleSvGwp('subscription', subPm);
    this.#renderSvPm(this.querySelector('[data-sv-pm="subscription"]'), subPm);
    this.#renderSvPmCompare(
      this.querySelector('[data-sv-pm-compare="subscription"]'),
      regular,
      subPm
    );

    if (!this.#additionalProduct) return;
    const addVariant = this.#targetVariantFor('additional_subscription');
    if (!addVariant) return;
    const addAlloc = this.#pickDisplayAllocation('additional_subscription', addVariant);
    const total = addAlloc?.price ?? addVariant.price;
    this.#toggleSvGwp('additional_subscription', total);
    const addPm = Math.round(total / this.#svMonths);
    this.#renderSvPm(this.querySelector('[data-sv-pm="additional_subscription"]'), addPm);
    this.#renderSvPmCompare(
      this.querySelector('[data-sv-pm-compare="additional_subscription"]'),
      regular,
      addPm
    );
  }

  /**
   * Show the gift-with-purchase line in an option's "What is Included" box
   * only when that option's order value reaches the threshold (host attribute
   * data-sv-gwp-min, cents; block setting sv_gwp_min). Mirrors the Liquid gate
   * in snippets/subscription-sv-after.liquid for the first paint.
   * @param {string} mode
   * @param {number | null} cents
   */
  #toggleSvGwp(mode, cents) {
    if (!this.#sectionId) return;
    const min = Number(this.dataset.svGwpMin);
    if (!Number.isFinite(min) || cents == null) return;
    const hide = cents < min;
    // Included-box line (after-section, moved below the ATC) and the card's
    // "+ gift" thumb pieces (snippets/subscription-sv-media.liquid).
    const nodes = [
      ...document.querySelectorAll(
        `[data-sv-after-section="${this.#sectionId}"] [data-sv-gwp="${mode}"]`
      ),
      ...this.querySelectorAll(`[data-sv-gwp="${mode}"]`),
    ];
    for (const n of nodes) n.toggleAttribute('hidden', hide);
  }

  /**
   * Money without a trailing ".00" ("$98"; "$32.67" keeps its cents).
   * @param {number} cents
   * @returns {string}
   */
  #svShortMoney(cents) {
    const formatted = this.#formatMoney(cents).trim();
    return formatted.endsWith('.00') ? formatted.slice(0, -3) : formatted;
  }

  /**
   * Fill the ATC button's price slots (snippets/add-to-cart-button.liquid)
   * with the selected option's total and compare-at. Emptied outside sv so
   * the control keeps its plain "Add to cart" label.
   */
  #updateAtcPrice() {
    const priceEl = this.#form?.querySelector('[data-subscription-atc-price]') ?? null;
    const compareEl = this.#form?.querySelector('[data-subscription-atc-compare]') ?? null;
    if (!priceEl && !compareEl) return;
    if (!this.#isSv()) {
      if (priceEl) priceEl.textContent = '';
      if (compareEl) compareEl.textContent = '';
      return;
    }
    /** @type {number | null} */
    let total = null;
    /** @type {number | null} */
    let compare = null;
    const mode = this.#selectedOption;
    if (mode === 'additional_subscription' && this.#additionalProduct) {
      const v = this.#targetVariantFor(mode);
      const alloc = v ? this.#pickDisplayAllocation(mode, v) : null;
      total = alloc?.price ?? v?.price ?? null;
      compare = v?.compare_at_price ?? null;
    } else {
      const v = this.#mainVariant();
      if (v) {
        if (mode === 'subscription') {
          const alloc = this.#pickDisplayAllocation('subscription', v);
          total = alloc?.price ?? v.price;
          compare =
            v.compare_at_price != null && v.compare_at_price > v.price
              ? v.compare_at_price
              : v.price;
        } else {
          total = v.price;
          compare = v.compare_at_price ?? null;
        }
      }
    }
    if (priceEl) priceEl.textContent = total == null ? '' : this.#svShortMoney(total);
    if (compareEl) {
      compareEl.textContent =
        total != null && compare != null && compare > total ? this.#svShortMoney(compare) : '';
    }
  }

  /**
   * Move the sv after-section (guarantee / included / benefits, rendered by
   * snippets/subscription-sv-after.liquid inside this element) to just
   * below the buy-once link — or the ATC block when there is no link — and
   * flag the group block that follows it (the v2 trust rows) so the sv CSS
   * can hide it. Idempotent; safe before and after morphs.
   */
  #placeSvAfter() {
    if (!this.#sectionId) return;
    const after = /** @type {HTMLElement | null} */ (
      document.querySelector(`[data-sv-after-section="${this.#sectionId}"]`)
    );
    if (!after) return;
    if (this.contains(after)) {
      const buyOnce = this.#findBuyOnceWrapper();
      /** @type {Element | null} */
      let anchor = buyOnce && !this.contains(buyOnce) ? buyOnce : null;
      if (!anchor) {
        const submitBtn = this.#form?.querySelector('button[type="submit"]') ?? null;
        const myParent = this.parentElement;
        let ancestor = submitBtn?.parentElement ?? null;
        while (ancestor && ancestor.parentElement !== myParent) ancestor = ancestor.parentElement;
        anchor = ancestor;
      }
      if (!anchor) return;
      anchor.after(after);
    }
    // The v1/v2 templates put a "trust rows" group (guarantee / shipping /
    // cancel-anytime icons) right after the buy buttons; the sv after-section
    // replaces it. Only flag a group that reads like one, never an arbitrary
    // neighbour.
    const next = after.nextElementSibling;
    if (
      next &&
      next.classList.contains('group-block') &&
      !next.hasAttribute('data-sv-hidden-row') &&
      /guarantee|shipping|cancel/i.test(next.textContent ?? '')
    ) {
      next.setAttribute('data-sv-hidden-row', '');
    }
  }

  /**
   * Resolve the main product's currently-active variant.
   * Mirrors the logic inside #targetVariantFor('subscription') but
   * exposed as a small accessor so both the onetime card (which has
   * no allocations) and the subscription card (which does) can share
   * the lookup.
   * @returns {any | null}
   */
  #mainVariant() {
    if (!this.#mainProduct) return null;
    return (
      this.#mainProduct.variants.find(
        (v) => Number(v.id) === this.#currentMainVariantId
      ) ??
      this.#mainProduct.variants[0] ??
      null
    );
  }

  /**
   * Pick the allocation to display in a card's price slot.
   *
   *   1. If the user has explicitly selected a plan AND the card's
   *      mode matches the selected mode → use the selected plan
   *      (so the dropdown's choice is reflected immediately).
   *   2. Else → recommended-position allocation (1-indexed) if valid.
   *   3. Else → first allocation.
   *
   * @param {SubOption} mode
   * @param {any} variant
   * @returns {any | null}
   */
  #pickDisplayAllocation(mode, variant) {
    const allocs = variant?.selling_plan_allocations ?? [];
    if (allocs.length === 0) return null;

    if (this.#selectedOption === mode && this.#selectedSellingPlanId != null) {
      const match = allocs.find(
        (a) => Number(a?.selling_plan_id) === this.#selectedSellingPlanId
      );
      if (match) return match;
    }

    const rec = this.#recommendedPosition();
    if (rec <= allocs.length) {
      return allocs[rec - 1];
    }

    return allocs[0];
  }

  /**
   * Render a formatted price into the card's main price span.
   * Output shape matches `snippets/subscription-price.liquid`:
   *   v1   → <span class="subscription-option__price-dollars">$99<sup class="subscription-option__price-cents">.00</sup></span>
   *   else → <span class="subscription-option__price-dollars">$99</span>
   *
   * @param {Element | null} span
   * @param {number | null | undefined} priceInCents
   * @param {string} [unit] Optional unit suffix, e.g. '/month' (v2).
   */
  #renderPrice(span, priceInCents, unit = '') {
    if (!span) return;
    if (priceInCents == null) {
      span.innerHTML = '';
      return;
    }
    span.innerHTML = this.#priceMarkup(priceInCents, unit);
  }

  /**
   * Render or hide the compare-at span.
   *
   * Shown only when `compareAtCents > gateCents` (MSRP strictly
   * greater than the variant's regular price). Otherwise the span
   * gets the `hidden` attribute + empty markup so CSS + AT both
   * skip it cleanly.
   *
   * A span Liquid stamped with `data-compare-at-suppressed` (v2
   * 3-month card without the bundle's
   * `subscription.show_compare_at_price` metafield) stays hidden no
   * matter what the price data says — the gate is decided
   * server-side once and JS must not undo it on variant/plan
   * switches.
   *
   * @param {Element | null} span
   * @param {number | null | undefined} compareAtCents
   * @param {number | null | undefined} gateCents
   * @param {string} [unit] Optional unit suffix, e.g. '/month' (v2).
   */
  #renderCompareAt(span, compareAtCents, gateCents, unit = '') {
    if (!span) return;
    if (
      span.hasAttribute('data-compare-at-suppressed') ||
      compareAtCents == null ||
      gateCents == null ||
      compareAtCents <= gateCents
    ) {
      span.setAttribute('hidden', '');
      span.innerHTML = '';
      return;
    }
    span.removeAttribute('hidden');
    span.innerHTML = this.#priceMarkup(compareAtCents, unit);
  }

  /**
   * Produce the inner markup for a price span at the given amount.
   * Shared by #renderPrice and #renderCompareAt so the two stay in
   * lockstep with the Liquid renderer.
   *
   * Uses Horizon's `formatMoney` with the shop's money_format +
   * currency (threaded through the context JSON), then splits on
   * the decimal separator exactly the way the Liquid filter chain
   * (`price | money | split: '.'`) does.
   *
   * v1 renders cents as a superscript; every other style (default,
   * v2) drops the cents entirely. An optional `unit` suffix (e.g.
   * '/month') renders as a SIBLING span after the dollars span — the
   * Liquid card markup emits
   * `{% render 'subscription-price' %}<span class="subscription-option__price-unit">/month</span>`;
   * keep the two in lockstep.
   *
   * @param {number} priceInCents
   * @param {string} [unit] Optional unit suffix, e.g. '/month'.
   * @returns {string}
   */
  #priceMarkup(priceInCents, unit = '') {
    const formatted = this.#formatMoney(priceInCents).trim();
    // Split at the LAST dot so formats that use "," for decimals
    // and "." for thousands (e.g. de-DE) don't mis-split.
    // Shopify's `money` filter always uses the locale-appropriate
    // separator; the Liquid snippet's `split: '.'` implicitly
    // assumes "." is the decimal, which holds for USD/en-US. We
    // prefer the slightly safer lastIndexOf('.') while matching
    // the USD output byte-for-byte.
    const idx = formatted.lastIndexOf('.');
    const dollars = idx === -1 ? formatted : formatted.slice(0, idx);
    const cents = idx === -1 ? '' : formatted.slice(idx + 1);

    const unitMarkup = unit
      ? `<span class="subscription-option__price-unit">${this.#escape(unit)}</span>`
      : '';

    if (this.#pricingBoxStyle === 'v1' && cents !== '') {
      return (
        `<span class="subscription-option__price-dollars">${this.#escape(dollars)}` +
        `<sup class="subscription-option__price-cents">.${this.#escape(cents)}</sup>` +
        `</span>${unitMarkup}`
      );
    }
    return `<span class="subscription-option__price-dollars">${this.#escape(dollars)}</span>${unitMarkup}`;
  }

  /**
   * @param {number} priceInCents
   * @returns {string}
   */
  #formatMoney(priceInCents) {
    const format = this.#context?.money_format || '${{amount}}';
    const currency = this.#context?.currency || 'USD';
    try {
      return formatMoney(priceInCents, format, currency);
    } catch {
      // Extremely defensive fallback — never throw inside a price
      // render path. USD-shaped output so the UI stays intact.
      const whole = Math.floor(priceInCents / 100);
      const rem = String(Math.abs(priceInCents) % 100).padStart(2, '0');
      return `$${whole}.${rem}`;
    }
  }

  /** Tiny HTML escaper for money strings (no DOM deps). */
  #escape(s) {
    return String(s)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }

  /* ────────────────────────────────────────────────────────────────
   * Capture-phase submit listener (belt-and-suspenders)
   *
   * For the bundle mode, the <input name="id"> in the form normally
   * holds the MAIN product's variant ID (managed by
   * product-form-component). #applyVariantIdOverride keeps it swapped
   * to the BUNDLE variant at all times — that proactive override is
   * the PRIMARY mechanism. This listener merely re-asserts both
   * inputs at submit time.
   *
   * ⚠️ Do not rely on ordering here: Horizon's component framework
   * delegates 'submit' at document level too and registers at theme
   * load (before this component connects), and its handleSubmit builds
   * `new FormData(form)` synchronously — so this listener can run
   * AFTER the payload is captured. Correctness comes from the inputs
   * already being right (proactive override), not from this hook.
   *
   * Matches the form by its data-type attribute + containment in our
   * .product-details scope (never by cached node identity — a
   * buy-buttons morph can replace the form node between caches).
   * ──────────────────────────────────────────────────────────────── */

  #bindSubmitListener() {
    // Only bind once — document listener is stable across morphs.
    if (this.#submitHandler) return;

    const handler = (/** @type {SubmitEvent} */ e) => {
      const t = e.target;
      // Ignore submits from other forms on the page (e.g. search, newsletter).
      if (!(t instanceof HTMLFormElement) || t.getAttribute('data-type') !== 'add-to-cart-form') return;
      const scope = this.closest('.product-details');
      if (scope && !scope.contains(t)) return;
      this.#onFormSubmit(t);
    };
    document.addEventListener('submit', handler, { capture: true });
    this.#submitHandler = handler;
  }

  #unbindSubmitListener() {
    if (this.#submitHandler) {
      document.removeEventListener('submit', this.#submitHandler, { capture: true });
      this.#submitHandler = null;
    }
  }

  /**
   * Convert "subscription add-to-cart" goal (theme setting convert_goal_subscription,
   * exposed by layout/theme.liquid as window.NOVEX_CONVERT.subscriptionGoal). Fired
   * when the form submits with a subscription or bundle option selected — the
   * subscription-rate proxy for the buy box test. No-op without the goal id or
   * the Convert queue.
   * @param {string | null} mode
   */
  #trackSubscriptionGoal(mode) {
    if (mode === 'onetime' || mode == null) return;
    const goal = window.NOVEX_CONVERT?.subscriptionGoal;
    if (!goal) return;
    window._conv_q = window._conv_q || [];
    window._conv_q.push(['triggerConversion', String(goal)]);
  }

  /** @param {HTMLFormElement} form The submitting add-to-cart form. */
  #onFormSubmit(form) {
    const mode = this.#selectedOption;
    this.#trackSubscriptionGoal(mode);
    // Only override for the bundle mode — main subscription and main
    // one-time already use the correct main-product variant ID.
    if (mode !== 'additional_subscription') return;

    const bundleVariant = this.#targetVariantFor(mode);
    if (!bundleVariant) return;

    // Re-assert [name="id"] with the bundle variant ID on the submitting
    // form itself (not a cached node).
    const variantInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="id"]')
    );
    if (variantInput) {
      variantInput.value = String(bundleVariant.id);
    }

    // Re-assert selling_plan too, in case a morph cleared it between
    // selection and submit.
    const planInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="selling_plan"][data-subscription-injected]')
    );
    if (planInput && this.#selectedSellingPlanId != null) {
      planInput.value = String(this.#selectedSellingPlanId);
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * Proactive variant-ID override
   *
   * Primary mechanism for bundle cart submissions. We keep [name="id"]
   * set to the correct variant at all times:
   *   bundle mode  → bundle variant ID (so product-form-component's
   *                  FormData already has the right value on first click)
   *   other modes  → main product variant ID (restore after leaving bundle)
   * Called on every radio change, variant change, and morph so the
   * value is always current.
   * ──────────────────────────────────────────────────────────────── */
  #applyVariantIdOverride() {
    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.#form?.querySelector('input[name="id"]') ?? null
    );
    if (!variantInput) return;

    const mode = this.#selectedOption;
    if (mode === 'additional_subscription') {
      const bundleVariant = this.#targetVariantFor(mode);
      if (bundleVariant) variantInput.value = String(bundleVariant.id);
    } else if (this.#currentMainVariantId != null) {
      // Switching away from bundle mode — restore to main product variant
      // so one-time and monthly submissions use the correct product.
      variantInput.value = String(this.#currentMainVariantId);
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * Quantity-selector visibility
   *
   * Subscription products on Recharge always charge for a fixed
   * quantity (the quantity-selector value is meaningless because the
   * recurring order is built off the selling-plan, not a per-cycle
   * quantity input). Hide the selector for any subscription mode and
   * show it for one-time only.
   *
   * Re-queried each call because morph after variant:update may have
   * replaced the <quantity-selector-component> element under our
   * cached pointer.
   * ──────────────────────────────────────────────────────────────── */
  #applyQuantityVisibility() {
    const productDetails = this.closest('.product-details');
    // Target the wrapper div rather than the inner component so that hiding it
    // removes it as a flex item entirely — no phantom gap before the ATC button.
    // Re-queried each call in case morph after variant:update replaced the element.
    const wrapper = /** @type {HTMLElement | null} */ (
      productDetails?.querySelector('.quantity-selector-wrapper') ?? null
    );
    this.#quantitySelector = /** @type {HTMLElement | null} */ (
      wrapper?.querySelector('quantity-selector-component') ?? null
    );
    if (!wrapper) return;

    // Hide for subscription modes, show for one-time.
    const hide = this.#selectedOption != null && this.#selectedOption !== 'onetime';
    wrapper.style.display = hide ? 'none' : '';

    // Reset quantity to 1 when switching to any subscription mode.
    // Recharge subscriptions are always quantity 1; leaving a user-set
    // value of e.g. 3 would silently add 3 subscription items to cart.
    if (hide) {
      const qtyInput = /** @type {HTMLInputElement | null} */ (
        this.#quantitySelector?.querySelector('input[name="quantity"]') ?? null
      );
      if (qtyInput && qtyInput.value !== '1') qtyInput.value = '1';
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * Info-modal show/hide
   *
   * Modal markup lives inside this block (rendered by
   * snippets/subscription-options-modal.liquid) and starts with the
   * `hidden` attribute. Toggling the attribute is sufficient — the
   * block's own CSS guards visibility off `[hidden]` so we don't have
   * to manage display: none inline.
   * ──────────────────────────────────────────────────────────────── */

  /**
   * Move all [data-subscription-popup] elements to <body> so they
   * escape the `position: sticky; z-index: 1` stacking context of
   * .product-details (.sticky-content--desktop).  Without this, the
   * fixed overlay is confined to that stacking context and loses to
   * the header (z-index: 8) in the global stack.
   *
   * Each modal is tagged with data-portal-for="<sectionId>" so:
   *  - #openModal / #closeAllOpenModals can scope queries per instance
   *  - disconnectedCallback can clean them up
   *  - updatedCallback can replace them after a morph re-creates them
   */
  #portalModals() {
    const sectionId = this.#sectionId;
    if (!sectionId) return;

    // Remove previously portalled copies (morph re-creates them inside
    // the component — drop the stale body copies before moving fresh ones).
    document
      .querySelectorAll(`[data-subscription-popup][data-portal-for="${sectionId}"]`)
      .forEach((m) => m.remove());

    // Move current modals from inside this element to <body>.
    this.querySelectorAll('[data-subscription-popup]').forEach((m) => {
      m.setAttribute('data-portal-for', sectionId);
      document.body.appendChild(m);
    });
  }

  /** @param {string} id */
  #openModal(id) {
    // Close any other open modal first so two info popups can't
    // visually overlap if a user rapid-clicks two triggers.
    this.#closeAllOpenModals();
    const sectionId = this.#sectionId ?? '';
    // Prefer the portalled copy in <body>; fall back to inside `this`
    // in case #portalModals hasn't run yet (e.g. during unit tests).
    const modal =
      document.querySelector(
        `[data-subscription-popup="${id}"][data-portal-for="${sectionId}"]`
      ) ?? this.querySelector(`[data-subscription-popup="${id}"]`);
    if (!modal) return;
    modal.removeAttribute('hidden');
  }

  /** @param {Element} modal */
  #closeModal(modal) {
    modal.setAttribute('hidden', '');
  }

  #closeAllOpenModals() {
    const sectionId = this.#sectionId ?? '';
    document
      .querySelectorAll(
        `[data-subscription-popup]:not([hidden])[data-portal-for="${sectionId}"]`
      )
      .forEach((m) => m.setAttribute('hidden', ''));
    // Also close any that weren't yet portalled (safety net).
    this.querySelectorAll('[data-subscription-popup]:not([hidden])').forEach((m) =>
      m.setAttribute('hidden', '')
    );
  }
}

if (!customElements.get('subscription-options')) {
  customElements.define('subscription-options', SubscriptionOptions);
}
